// ─────────────────────────────────────────────────────────────────────────────
// Enum labels — the words a person reads where the database stores a code.
//
// Before this file the interface showed raw column values with underscores
// swapped for spaces: an Arabic UI offering "mutual agreement" and "dismissal
// with cause" in the termination dropdown. That is not cosmetic. The
// termination reason decides end-of-service entitlement under Articles 84/85,
// so somebody choosing from a list they cannot read is choosing what an
// employee gets paid.
//
// Keys are `<group>_<value>`, so a call site writes:
//
//     t(`enums.terminationReason_${reason}`)
//
// and TypeScript checks it. `reason` is a union of literals, so the template
// literal expands to a union of keys and is verified against `TranslationKey`
// — a value with no label here is a COMPILE error, not a raw code leaking into
// the screen at runtime. That is the whole point of doing it this way rather
// than with a lookup map and a fallback.
// ─────────────────────────────────────────────────────────────────────────────

export const enums = {
  en: {
    // Decides the Article 84/85 entitlement. The most consequential list here.
    terminationReason_resignation: "Resignation",
    terminationReason_end_of_term: "End of contract term",
    terminationReason_mutual_agreement: "Mutual agreement",
    terminationReason_probation: "Ended during probation (Article 53)",
    terminationReason_dismissal_with_cause: "Dismissal for cause (Article 80)",
    terminationReason_redundancy: "Redundancy",
    terminationReason_retirement: "Retirement",
    terminationReason_death: "Death",

    contractType_fixed_term: "Fixed term",
    contractType_unlimited: "Indefinite",

    contractStatus_draft: "Draft",
    contractStatus_active: "Active",
    contractStatus_expired: "Expired",
    contractStatus_terminated: "Terminated",
    contractStatus_superseded: "Superseded",

    employeeStatus_active: "Active",
    employeeStatus_on_leave: "On leave",
    employeeStatus_suspended: "Suspended",
    employeeStatus_terminated: "Terminated",

    erpRole_super_admin: "Super administrator",
    erpRole_hr_manager: "HR manager",
    erpRole_branch_manager: "Branch manager",
    erpRole_department_manager: "Department manager",
    erpRole_employee: "Employee",

    transferMethod_bank_transfer: "Bank transfer",
    transferMethod_mudad: "Mudad",
    transferMethod_cash: "Cash",

    payrollRunStatus_draft: "Draft",
    payrollRunStatus_approved: "Approved",
    payrollRunStatus_paid: "Paid",

    leaveRequestStatus_pending: "Pending",
    leaveRequestStatus_approved: "Approved",
    leaveRequestStatus_rejected: "Rejected",
    leaveRequestStatus_cancelled: "Cancelled",

    overtimeStatus_pending: "Pending",
    overtimeStatus_approved: "Approved",
    overtimeStatus_rejected: "Rejected",
    overtimeStatus_cancelled: "Cancelled",

    requestStatus_pending: "Pending",
    requestStatus_approved: "Approved",
    requestStatus_rejected: "Rejected",
    requestStatus_cancelled: "Cancelled",

    requestKind_late_arrival: "Late arrival",
    requestKind_attendance_correction: "Attendance correction",
    requestKind_salary_advance: "Salary advance",

    loanStatus_active: "Active",
    loanStatus_paused: "Paused",
    loanStatus_settled: "Settled",
    loanStatus_cancelled: "Cancelled",

    leaveAdjustmentKind_opening_balance: "Opening balance",
    leaveAdjustmentKind_correction: "Correction",
    leaveAdjustmentKind_encashment: "Encashment",
    leaveAdjustmentKind_settlement: "Settlement",
  },
  ar: {
    terminationReason_resignation: "استقالة",
    terminationReason_end_of_term: "انتهاء مدة العقد",
    terminationReason_mutual_agreement: "إنهاء بالتراضي",
    terminationReason_probation: "إنهاء خلال فترة التجربة (المادة 53)",
    terminationReason_dismissal_with_cause: "فصل لسبب مشروع (المادة 80)",
    terminationReason_redundancy: "الاستغناء عن الوظيفة",
    terminationReason_retirement: "التقاعد",
    terminationReason_death: "الوفاة",

    contractType_fixed_term: "محدد المدة",
    contractType_unlimited: "غير محدد المدة",

    contractStatus_draft: "مسودة",
    contractStatus_active: "ساري",
    contractStatus_expired: "منتهي",
    contractStatus_terminated: "منتهية خدمته",
    contractStatus_superseded: "مستبدل",

    employeeStatus_active: "على رأس العمل",
    employeeStatus_on_leave: "في إجازة",
    employeeStatus_suspended: "موقوف",
    employeeStatus_terminated: "منتهية خدمته",

    erpRole_super_admin: "مدير النظام",
    erpRole_hr_manager: "مدير الموارد البشرية",
    erpRole_branch_manager: "مدير فرع",
    erpRole_department_manager: "مدير قسم",
    erpRole_employee: "موظف",

    transferMethod_bank_transfer: "تحويل بنكي",
    transferMethod_mudad: "مُدد",
    transferMethod_cash: "نقداً",

    payrollRunStatus_draft: "مسودة",
    payrollRunStatus_approved: "معتمد",
    payrollRunStatus_paid: "مدفوع",

    leaveRequestStatus_pending: "قيد الانتظار",
    leaveRequestStatus_approved: "معتمد",
    leaveRequestStatus_rejected: "مرفوض",
    leaveRequestStatus_cancelled: "ملغى",

    overtimeStatus_pending: "قيد الانتظار",
    overtimeStatus_approved: "معتمد",
    overtimeStatus_rejected: "مرفوض",
    overtimeStatus_cancelled: "ملغى",

    requestStatus_pending: "قيد الانتظار",
    requestStatus_approved: "معتمد",
    requestStatus_rejected: "مرفوض",
    requestStatus_cancelled: "ملغى",

    requestKind_late_arrival: "تأخر عن الدوام",
    requestKind_attendance_correction: "تصحيح حضور",
    requestKind_salary_advance: "سلفة على الراتب",

    loanStatus_active: "قائمة",
    loanStatus_paused: "موقوفة",
    loanStatus_settled: "مسددة",
    loanStatus_cancelled: "ملغاة",

    leaveAdjustmentKind_opening_balance: "رصيد افتتاحي",
    leaveAdjustmentKind_correction: "تصحيح",
    leaveAdjustmentKind_encashment: "صرف بدل",
    leaveAdjustmentKind_settlement: "تسوية نهائية",
  },
} as const;
